import React from 'react';
import DayCell from './DayCell';
import styles from './MonthView.module.css';

const DAY_NAMES_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function getMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - startDate.getDay());

  const endDate = new Date(lastDay);
  if (endDate.getDay() !== 6) {
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
  }

  const days = [];
  const current = new Date(startDate);
  while (current <= endDate) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
}

function dateKey(date) {
  return date.toISOString().split('T')[0];
}

function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

// Unit band color palette (cycle through for multiple units)
const UNIT_COLORS = [
  { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af' },  // blue
  { bg: '#f0fdf4', border: '#22c55e', text: '#166534' },  // green
  { bg: '#fdf4ff', border: '#a855f7', text: '#6b21a8' },  // purple
  { bg: '#fff7ed', border: '#f97316', text: '#9a3412' },  // orange
  { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },  // red
  { bg: '#ecfeff', border: '#06b6d4', text: '#155e75' },  // cyan
  { bg: '#fefce8', border: '#eab308', text: '#854d0e' },  // yellow
  { bg: '#f0fdfa', border: '#14b8a6', text: '#115e59' },  // teal
  { bg: '#faf5ff', border: '#8b5cf6', text: '#5b21b6' },  // violet
];

function getUnitForDate(dateKey, units) {
  if (!units || units.length === 0) return null;
  for (const unit of units) {
    if (!unit.startDate || !unit.endDate) continue;
    const start = new Date(unit.startDate).toISOString().split('T')[0];
    const end = new Date(unit.endDate).toISOString().split('T')[0];
    if (dateKey >= start && dateKey <= end) {
      const colorIndex = ((unit.sequenceOrder || 1) - 1) % UNIT_COLORS.length;
      return { ...unit, color: UNIT_COLORS[colorIndex] };
    }
  }
  return null;
}

function MonthView({ currentDate, lessons, worksheets = [], units, onDayClick, onLessonClick, showWeekends, holidayMap }) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const allDays = getMonthGrid(year, month);

  const dayNames = showWeekends ? DAY_NAMES_FULL : WEEKDAY_NAMES;
  const days = showWeekends ? allDays : allDays.filter(d => !isWeekend(d));
  const columnsPerRow = showWeekends ? 7 : 5;

  const today = new Date();
  const todayStr = dateKey(today);

  // Group lessons by date
  const lessonsByDate = {};
  for (const lesson of lessons) {
    if (lesson.scheduledDate) {
      const key = new Date(lesson.scheduledDate).toISOString().split('T')[0];
      if (!lessonsByDate[key]) lessonsByDate[key] = [];
      lessonsByDate[key].push(lesson);
    }
  }

  // Group worksheets by date
  const worksheetsByDate = {};
  for (const worksheet of worksheets) {
    if (worksheet.scheduledDate) {
      const key = new Date(worksheet.scheduledDate).toISOString().split('T')[0];
      if (!worksheetsByDate[key]) worksheetsByDate[key] = [];
      worksheetsByDate[key].push(worksheet);
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.weekHeader} style={{ gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)` }}>
        {dayNames.map((name) => (
          <div key={name} className={styles.dayName}>{name}</div>
        ))}
      </div>
      <div className={styles.grid} style={{ gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)` }}>
        {days.map((day, index) => {
          const key = dateKey(day);
          const activeUnit = getUnitForDate(key, units);
          return (
            <DayCell
              key={key}
              date={day}
              lessons={lessonsByDate[key] || []}
              worksheets={worksheetsByDate[key] || []}
              isToday={key === todayStr}
              isCurrentMonth={day.getMonth() === month}
              isWeekend={isWeekend(day)}
              isLastInRow={(index + 1) % columnsPerRow === 0}
              holiday={holidayMap[key] || null}
              activeUnit={activeUnit}
              onClick={onDayClick}
              onLessonClick={onLessonClick}
            />
          );
        })}
      </div>
    </div>
  );
}

export default MonthView;
